import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { FilmStrip, TrendUp, Users, CurrencyDollar, ArrowLeft, SealCheck, ArrowSquareOut, User as UserIcon, ClockCounterClockwise } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { parseInrInput, formatInrFriendly } from '../lib/inr';
import BackstagePassPanel from '../components/BackstagePassPanel';
import FanLeaderboard from '../components/FanLeaderboard';
import Modal from '../components/Modal';
import { setNativeTitle } from '../lib/native';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MovieDetail = ({ user }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [movie, setMovie] = useState(null);
  const [investments, setInvestments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTier, setSelectedTier] = useState(null);
  const [investAmount, setInvestAmount] = useState('');
  const [investing, setInvesting] = useState(false);
  const [crew, setCrew] = useState([]);
  const [producerProfile, setProducerProfile] = useState(null);
  const [pappaSettlement, setPappaSettlement] = useState(null);
  const [showSettleModal, setShowSettleModal] = useState(false);
  const [settleAmount, setSettleAmount] = useState('');
  const [settleNotes, setSettleNotes] = useState('');
  const [pappaInvestOpen, setPappaInvestOpen] = useState(false);
  const [pappaInvestAmount, setPappaInvestAmount] = useState('');

  useEffect(() => {
    fetchMovie();
    fetchInvestments();
    fetchCrew();
    fetchPappaSettlement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchPappaSettlement = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/movies/${id}/pappa/settlement`);
      setPappaSettlement(res.data || null);
    } catch (err) { /* optional */ }
  };

  useEffect(() => {
    if (movie?.title) setNativeTitle(movie.title);
  }, [movie?.title]);

  useEffect(() => {
    if (movie?.producer_id) {
      fetchProducerProfile(movie.producer_id);
    }
  }, [movie?.producer_id]);

  const fetchCrew = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/movies/${id}/crew`);
      setCrew(res.data || []);
    } catch (err) {
      // crew is optional
    }
  };

  const fetchProducerProfile = async (producerId) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/producers/${producerId}/profile`);
      setProducerProfile(res.data);
    } catch (err) {
      // producer track record optional
    }
  };

  const fetchMovie = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/movies/${id}`);
      setMovie(response.data);
      if (response.data.investment_tiers.length > 0) {
        setSelectedTier(response.data.investment_tiers[0]);
        setInvestAmount(response.data.investment_tiers[0].min_amount.toString());
      }
    } catch (error) {
      toast.error('Failed to load movie');
    } finally {
      setLoading(false);
    }
  };

  const fetchInvestments = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/investments/movie/${id}`);
      setInvestments(response.data);
    } catch (error) {
      // Non-critical: leave investments empty if fetch fails
    }
  };

  const handleInvest = async () => {
    if (!user) {
      toast.error('Please login to invest');
      navigate('/login');
      return;
    }
    if (!selectedTier) {
      toast.error('Please select an investment tier');
      return;
    }

    setInvesting(true);
    try {
      const amountNum = investAmount ? parseInrInput(investAmount) : selectedTier.min_amount;
      if (!Number.isFinite(amountNum) || amountNum <= 0) {
        toast.error('Please enter a valid investment amount');
        setInvesting(false);
        return;
      }
      if (amountNum < selectedTier.min_amount) {
        toast.error(`Minimum for ${selectedTier.name} is ₹${selectedTier.min_amount.toLocaleString('en-IN')}`);
        setInvesting(false);
        return;
      }
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${BACKEND_URL}/api/payments/investment/checkout`,
        {
          movie_id: id,
          tier_name: selectedTier.name,
          amount: amountNum,
          origin_url: window.location.origin,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = res.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Checkout failed');
      setInvesting(false);
    }
  };

  const handleFanPledge = async (months) => {
    if (!user) {
      toast.error('Please login to back this movie');
      navigate('/login');
      return;
    }
    setInvesting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${BACKEND_URL}/api/payments/fan-pledge/checkout`,
        {
          movie_id: id,
          months,
          origin_url: window.location.origin,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = res.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Pledge failed');
      setInvesting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background" role="status" aria-live="polite">
        <span className="sr-only">Loading movie</span>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-6" aria-hidden="true">
          <div className="fz-skeleton aspect-video rounded-lg" />
          <div className="fz-skeleton h-10 w-2/3 rounded" />
          <div className="fz-skeleton h-4 w-full rounded" />
          <div className="fz-skeleton h-4 w-5/6 rounded" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="fz-skeleton h-28 rounded-lg" />
            <div className="fz-skeleton h-28 rounded-lg" />
            <div className="fz-skeleton h-28 rounded-lg" />
            <div className="fz-skeleton h-28 rounded-lg" />
          </div>
        </div>
      </div>
    );
  }

  if (!movie) return null;

  const fundingPct = Math.min((movie.raised_amount / movie.target_amount) * 100, 100);
  const totalInvestors = new Set(investments.map(i => i.user_id)).size;

  return (
    <div className="min-h-screen bg-background">
      <div className="noise-overlay" />
      
      {/* Navbar */}
      <nav data-web-chrome="nav" className="relative z-10 glass-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <button onClick={() => navigate('/marketplace')} data-testid="nav-back-btn" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
              <ArrowLeft size={24} className="text-gold" weight="bold" />
              <span className="font-body font-medium">Back to Marketplace</span>
            </button>
            {user && (
              <button
                data-testid="nav-dashboard-btn"
                onClick={() => navigate(
                  user.role === 'producer' ? '/producer-dashboard' : 
                  user.role === 'fan' ? '/fan-dashboard' : 
                  '/investor-dashboard'
                )}
                className="btn-primary"
              >
                Dashboard
              </button>
            )}
          </div>
        </div>
      </nav>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left: Movie Info */}
          <div className="lg:col-span-2 space-y-8">
            {/* Hero Image */}
            <div className="relative aspect-video rounded-lg overflow-hidden native-movie-hero">
              <img src={movie.poster_url} alt={`${movie.title} poster`} className="w-full h-full object-cover" />
              <div className="native-hero-fade" aria-hidden="true" />
              <div className="absolute top-4 left-4">
                <span className="inline-block px-4 py-2 bg-gold text-black rounded-sm font-bold uppercase tracking-wide text-sm">
                  {movie.status}
                </span>
              </div>
            </div>

            {/* Title & Description */}
            <div>
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <h1 className="font-heading text-5xl font-bold w-full sm:w-auto" data-testid="movie-title">{movie.title}</h1>
                <span className="px-3 py-1 bg-gold text-black rounded-sm font-bold text-sm">
                  {movie.industry}
                </span>
                <span className="px-3 py-1 bg-white/5 border border-white/10 rounded-sm text-slate-400 text-sm">
                  {movie.genre}
                </span>
              </div>
              <p className="font-body text-lg text-slate-400 leading-relaxed" data-testid="movie-description">
                {movie.description}
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-card border border-white/5 rounded-lg p-6">
                <CurrencyDollar size={32} className="text-gold mb-3" weight="bold" />
                <div className="font-body text-xs text-slate-400 mb-1">Budget</div>
                <div className="font-mono text-2xl font-bold tabular-nums">₹{(movie.budget / 1000000).toFixed(1)}M</div>
              </div>
              <div className="bg-card border border-white/5 rounded-lg p-6">
                <TrendUp size={32} className="text-gold mb-3" weight="bold" />
                <div className="font-body text-xs text-slate-400 mb-1">Raised</div>
                <div className="font-mono text-2xl font-bold tabular-nums">₹{(movie.raised_amount / 1000).toFixed(0)}K</div>
              </div>
              <div className="bg-card border border-white/5 rounded-lg p-6">
                <Users size={32} className="text-gold mb-3" weight="bold" />
                <div className="font-body text-xs text-slate-400 mb-1">Investors</div>
                <div className="font-mono text-2xl font-bold tabular-nums">{totalInvestors}</div>
              </div>
              <div className="bg-card border border-white/5 rounded-lg p-6">
                <FilmStrip size={32} className="text-gold mb-3" weight="bold" />
                <div className="font-body text-xs text-slate-400 mb-1">Progress</div>
                <div className="font-mono text-2xl font-bold tabular-nums">{fundingPct.toFixed(0)}%</div>
              </div>
            </div>

            {/* Funding Options (FUNDER / PAPPA / KUBERA tracks) */}
            <div className="bg-card border border-white/10 rounded-lg p-6" data-testid="funding-options-section">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h3 className="font-heading text-xl font-bold">FUNDING <span className="text-gold">OPTIONS</span></h3>
                {user && (user.role === 'admin' || user.id === movie.producer_id) && (
                  <button
                    onClick={() => navigate(`/movie/${movie.id}/funding/setup`)}
                    className="btn-secondary text-xs py-2 px-3"
                    data-testid="manage-funding-link"
                  >
                    Configure
                  </button>
                )}
              </div>
              {(!movie.funding_modes || movie.funding_modes.length === 0) ? (
                <p className="font-body text-sm text-slate-400" data-testid="no-funding-modes">No funding modes configured yet.</p>
              ) : (
                <div className="space-y-4">
                  {movie.funding_modes.includes('FUNDER') && movie.funder && (
                    <div className="border border-cyan-500/30 bg-cyan-500/[0.04] rounded-lg p-4" data-testid="track-FUNDER">
                      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                        <div>
                          <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-cyan-500/15 text-cyan-300 border border-cyan-500/30">FUNDER</span>
                          <span className="ml-2 text-sm text-slate-300 font-body">Fixed-interest debt instrument</span>
                        </div>
                        <span className="font-mono text-sm tabular-nums">₹{formatInrFriendly(movie.funder.raised_amount_inr || 0)} / ₹{formatInrFriendly(movie.funder.target_amount_inr)}</span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <div className="bg-black/30 rounded p-3 border border-white/5">
                          <div className="flex items-baseline justify-between">
                            <span className="font-heading font-bold text-sm">Bronze</span>
                            <span className="font-mono text-xs text-cyan-300">10% p.a.</span>
                          </div>
                          <div className="text-[10px] text-slate-400 mt-1">Min ₹10K · 12 months</div>
                        </div>
                        <div className="bg-black/30 rounded p-3 border border-white/5">
                          <div className="flex items-baseline justify-between">
                            <span className="font-heading font-bold text-sm">Silver</span>
                            <span className="font-mono text-xs text-cyan-300">12% p.a.</span>
                          </div>
                          <div className="text-[10px] text-slate-400 mt-1">Min ₹1L · 24 months</div>
                        </div>
                        <div className="bg-black/30 rounded p-3 border border-white/5">
                          <div className="flex items-baseline justify-between">
                            <span className="font-heading font-bold text-sm">Gold</span>
                            <span className="font-mono text-xs text-cyan-300">14% p.a.</span>
                          </div>
                          <div className="text-[10px] text-slate-400 mt-1">Min ₹10L · 36 months</div>
                        </div>
                      </div>
                      <p className="text-[10px] text-slate-400 mt-2 font-body">Use the INVEST panel on the right to pick a tier and pay via Stripe.</p>
                    </div>
                  )}

                  {movie.funding_modes.includes('PAPPA') && movie.pappa && (
                    <div className="border border-amber-500/30 bg-amber-500/[0.04] rounded-lg p-4" data-testid="track-PAPPA">
                      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                        <div>
                          <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-amber-500/15 text-amber-300 border border-amber-500/30">PAPPA</span>
                          <span className="ml-2 text-sm text-slate-300 font-body">Pre-Approved Purchase + markup</span>
                          {movie.pappa.status === 'pending' && <span className="ml-2 inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-amber-500/15 text-amber-300 border border-amber-500/30">Pending verification</span>}
                          {movie.pappa.status === 'rejected' && <span className="ml-2 inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-rose-500/15 text-rose-300 border border-rose-500/30">Rejected</span>}
                          {movie.pappa.status === 'active' && <span className="ml-2 inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">✓ Verified</span>}
                        </div>
                      </div>

                      {movie.pappa.status === 'pending' && (!user || (user.id !== movie.producer_id && user.role !== 'admin')) ? (
                        <p className="text-sm text-slate-400 font-body" data-testid="pappa-pending-investor-msg">This PAPPA track is awaiting admin verification of the OTT Purchase Order. It will go live once approved.</p>
                      ) : (
                        <>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div className="bg-black/30 rounded p-3 border border-white/5">
                              <div className="text-[10px] uppercase text-slate-400">OTT partner</div>
                              <div className="font-body text-sm font-bold">{movie.pappa.ott_partner_name}</div>
                            </div>
                            <div className="bg-black/30 rounded p-3 border border-white/5">
                              <div className="text-[10px] uppercase text-slate-400">PO from OTT (100%)</div>
                              <div className="font-mono text-sm font-bold tabular-nums">₹{formatInrFriendly(movie.pappa.ott_po_amount_inr)}</div>
                            </div>
                            <div className="bg-black/30 rounded p-3 border border-white/5">
                              <div className="text-[10px] uppercase text-slate-400">Investors put in</div>
                              <div className="font-mono text-sm font-bold tabular-nums text-amber-300">₹{formatInrFriendly(movie.pappa.raise_amount_inr)}</div>
                            </div>
                            <div className="bg-black/30 rounded p-3 border border-white/5">
                              <div className="text-[10px] uppercase text-slate-400">Markup paid out</div>
                              <div className="font-mono text-sm font-bold tabular-nums text-emerald-400">+{movie.pappa.markup_pct}%</div>
                            </div>
                          </div>
                          <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
                            <div className="text-xs text-slate-400 font-body">
                              Raised ₹{formatInrFriendly(movie.pappa.raised_amount_inr || 0)} of ₹{formatInrFriendly(movie.pappa.raise_amount_inr)}
                              {movie.pappa.status === 'settled' && <span className="ml-2 text-emerald-300">· ✓ Settled — OTT paid ₹{formatInrFriendly(movie.pappa.ott_received_inr || 0)}</span>}
                            </div>
                            <div className="flex gap-2 flex-wrap">
                              {movie.pappa.status === 'active' && user && user.role === 'investor' && (
                                <button data-testid="pappa-invest-btn" className="btn-primary text-xs py-1.5 px-3" onClick={() => setPappaInvestOpen(true)}>
                                  Back as Associate
                                </button>
                              )}
                              {movie.pappa.status === 'active' && user && (user.id === movie.producer_id || user.role === 'admin') && (movie.pappa.raised_amount_inr || 0) > 0 && (
                                <button data-testid="pappa-settle-btn" onClick={() => {
                                  const expected = (movie.pappa.raised_amount_inr || 0) * (1 + movie.pappa.markup_pct / 100);
                                  setSettleAmount(String(expected));
                                  setSettleNotes('');
                                  setShowSettleModal(true);
                                }} className="text-xs px-3 py-1.5 rounded bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/25 transition-colors">
                                  Settle PAPPA (OTT paid)
                                </button>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {movie.funding_modes.includes('KUBERA') && movie.kubera && (
                    <div className="border border-purple-500/30 bg-purple-500/[0.04] rounded-lg p-4" data-testid="track-KUBERA">
                      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                        <div>
                          <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-purple-500/15 text-purple-300 border border-purple-500/30">KUBERA</span>
                          <span className="ml-2 text-sm text-slate-300 font-body">OTT-rights profit share only</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="bg-black/30 rounded p-3 border border-white/5">
                          <div className="text-[10px] uppercase text-slate-400">OTT share to pool</div>
                          <div className="font-mono text-sm font-bold tabular-nums text-purple-300">{movie.kubera.ott_profit_share_pct}%</div>
                        </div>
                        <div className="bg-black/30 rounded p-3 border border-white/5">
                          <div className="text-[10px] uppercase text-slate-400">Target raise</div>
                          <div className="font-mono text-sm font-bold tabular-nums">₹{formatInrFriendly(movie.kubera.target_amount_inr)}</div>
                        </div>
                        <div className="bg-black/30 rounded p-3 border border-white/5">
                          <div className="text-[10px] uppercase text-slate-400">Raised so far</div>
                          <div className="font-mono text-sm font-bold tabular-nums">₹{formatInrFriendly(movie.kubera.raised_amount_inr || 0)}</div>
                        </div>
                        <div className="bg-black/30 rounded p-3 border border-white/5">
                          <div className="text-[10px] uppercase text-slate-400">Est. OTT sale</div>
                          <div className="font-mono text-sm font-bold tabular-nums text-emerald-400">
                            {movie.kubera.estimated_ott_sale_inr ? `₹${formatInrFriendly(movie.kubera.estimated_ott_sale_inr)}` : '—'}
                          </div>
                        </div>
                      </div>
                      <p className="text-[10px] text-slate-400 mt-3 font-body">No equity in theatrical, music, or merchandise revenue. Returns only realised after OTT sale is closed.</p>
                      <div className="mt-2 flex justify-end">
                        <button data-testid="kubera-invest-btn" className="btn-primary text-xs py-1.5 px-3" onClick={() => toast('KUBERA Stripe checkout coming in the next iteration — meanwhile contact the producer.')}>
                          Back as KUBERA (Stripe — coming next)
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Producer Track Record */}
            <div className="bg-card border border-white/10 rounded-lg p-6" data-testid="producer-track-record-card">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h3 className="font-heading text-xl font-bold">ABOUT THE PRODUCER</h3>
                {producerProfile?.stats?.verified_projects > 0 && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded text-xs font-medium border bg-emerald-500/10 text-emerald-300 border-emerald-500/30">
                    <SealCheck size={12} weight="bold" /> Verified track record
                  </span>
                )}
              </div>
              <div className="flex items-center space-x-4 mb-5">
                <div className="w-16 h-16 bg-gold/20 rounded-full flex items-center justify-center">
                  <span className="font-heading text-2xl font-bold text-gold">
                    {movie.producer_name.charAt(0)}
                  </span>
                </div>
                <div className="flex-1">
                  <div className="font-body font-bold text-lg">{movie.producer_name}</div>
                  {producerProfile?.profile?.company_name && (
                    <div className="font-body text-sm text-gold">{producerProfile.profile.company_name}</div>
                  )}
                  {producerProfile?.profile?.location && (
                    <div className="font-body text-xs text-slate-400">{producerProfile.profile.location}</div>
                  )}
                </div>
                <button
                  data-testid="view-producer-profile-btn"
                  onClick={() => navigate(`/producer/${movie.producer_id}`)}
                  className="btn-secondary text-xs py-2 px-3 inline-flex items-center gap-1"
                >
                  Full profile <ArrowSquareOut size={12} />
                </button>
              </div>

              {producerProfile?.profile?.bio && (
                <p className="font-body text-sm text-slate-300 mb-4">{producerProfile.profile.bio}</p>
              )}

              {producerProfile?.stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="producer-stats-inline">
                  <div className="bg-white/[0.03] border border-white/10 rounded p-3" data-testid="stat-verified">
                    <div className="text-[10px] uppercase tracking-wide text-slate-400">Verified projects</div>
                    <div className="font-mono text-lg font-bold tabular-nums">{producerProfile.stats.verified_projects}</div>
                  </div>
                  <div className="bg-white/[0.03] border border-white/10 rounded p-3" data-testid="stat-avg-roi">
                    <div className="text-[10px] uppercase tracking-wide text-slate-400">Avg ROI</div>
                    <div className={`font-mono text-lg font-bold tabular-nums ${producerProfile.stats.avg_roi_pct >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                      {producerProfile.stats.avg_roi_pct > 0 ? '+' : ''}{producerProfile.stats.avg_roi_pct}%
                    </div>
                  </div>
                  <div className="bg-white/[0.03] border border-white/10 rounded p-3" data-testid="stat-box-office">
                    <div className="text-[10px] uppercase tracking-wide text-slate-400">Total box office</div>
                    <div className="font-mono text-lg font-bold tabular-nums text-cyan-400">₹{formatInrFriendly(producerProfile.stats.total_box_office_inr)}</div>
                  </div>
                  <div className="bg-white/[0.03] border border-white/10 rounded p-3" data-testid="stat-on-time">
                    <div className="text-[10px] uppercase tracking-wide text-slate-400">On-time delivery</div>
                    <div className="font-mono text-lg font-bold tabular-nums">
                      {producerProfile.stats.on_time_delivery_rate == null ? '—' : `${producerProfile.stats.on_time_delivery_rate}%`}
                    </div>
                  </div>
                </div>
              )}

              {producerProfile?.verified_projects?.length > 0 && (
                <div data-testid="recent-verified-projects">
                  <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-2 font-body">Recent verified projects</div>
                  <div className="space-y-2">
                    {producerProfile.verified_projects.slice(0, 3).map(p => (
                      <div key={p.id} className="flex items-center justify-between gap-3 bg-white/[0.02] border border-white/5 rounded p-3" data-testid={`recent-project-${p.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-body text-sm font-bold truncate">{p.title}</span>
                            <span className="text-[10px] text-slate-400">{p.industry} · {p.release_date?.slice(0, 4)}</span>
                            {p.on_time === true && <ClockCounterClockwise size={10} className="text-emerald-400" />}
                          </div>
                          <div className="text-[10px] text-slate-400 mt-0.5">{p.role} · ₹{formatInrFriendly(p.budget_inr)} budget → ₹{formatInrFriendly(p.box_office_inr)} box</div>
                        </div>
                        <span className={`font-mono text-sm font-bold tabular-nums ${p.roi_pct >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                          {p.roi_pct > 0 ? '+' : ''}{p.roi_pct}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(!producerProfile?.stats || producerProfile.stats.verified_projects === 0) && (
                <div className="bg-amber-500/5 border border-amber-500/20 rounded p-3 text-xs text-amber-200 font-body" data-testid="no-track-record-warning">
                  ⚠ This producer has no verified track record yet. Invest with caution and review the project details thoroughly.
                </div>
              )}
            </div>

            {/* Fan Leaderboard */}
            <FanLeaderboard movieId={movie.id} movieTitle={movie.title} />

            {/* Backstage Pass */}
            <BackstagePassPanel movieId={movie.id} movieTitle={movie.title} user={user} />

            {/* Crew & Cast */}
            <div className="bg-card border border-white/10 rounded-lg p-6" data-testid="crew-and-cast-section">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-xl font-bold">CREW &amp; CAST</h3>
                {user && (user.role === 'admin' || user.id === movie.producer_id) && (
                  <button
                    onClick={() => navigate(`/movie/${movie.id}/crew/manage`)}
                    className="btn-secondary text-xs py-2 px-3"
                    data-testid="manage-crew-link"
                  >
                    Manage
                  </button>
                )}
              </div>
              {crew.length === 0 ? (
                <p className="font-body text-sm text-slate-400" data-testid="no-crew-msg">No crew details have been added yet.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="crew-grid">
                  {crew.map(c => (
                    <div key={c.id} className="flex items-start gap-3 bg-white/[0.02] border border-white/5 rounded p-3" data-testid={`crew-card-${c.id}`}>
                      {c.photo_url ? (
                        <img src={`${c.photo_url.startsWith('http') ? '' : BACKEND_URL}${c.photo_url}`} alt={c.name} className="w-12 h-12 rounded-full object-cover ring-1 ring-white/10" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-white/5 ring-1 ring-white/10 flex items-center justify-center">
                          <UserIcon size={20} className="text-slate-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-body text-sm font-bold truncate">{c.name}</span>
                          <span className="text-[10px] uppercase tracking-wide text-gold border border-gold/30 bg-gold/10 px-1.5 py-0.5 rounded font-bold">
                            {c.role === 'Other' ? (c.custom_role || 'Other') : c.role}
                          </span>
                        </div>
                        {c.known_for && <div className="text-[11px] text-slate-300 mt-0.5">Known for: {c.known_for}</div>}
                        {c.bio && <div className="text-[10px] text-slate-400 mt-0.5 line-clamp-2">{c.bio}</div>}
                        {c.profile_url && (
                          <a href={c.profile_url} target="_blank" rel="noreferrer" className="text-[10px] text-gold hover:underline mt-1 inline-flex items-center gap-0.5">
                            Profile <ArrowSquareOut size={9} />
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right: Investment Panel */}
          <div className="lg:col-span-1">
            <div className="bg-card border border-white/5 rounded-lg p-8 sticky top-24 native-invest-dock">
              <h2 className="font-heading text-2xl font-bold mb-6">INVEST NOW</h2>
              
              {/* Funding Progress */}
              <div className="mb-8">
                <div className="flex justify-between items-baseline mb-2">
                  <span className="font-body text-sm text-slate-400">Funding Goal</span>
                  <span className="font-mono text-xl font-bold text-gold tabular-nums">
                    ₹{(movie.target_amount / 1000).toFixed(0)}K
                  </span>
                </div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden mb-2">
                  <div
                    className="h-full bg-gold transition-all duration-500"
                    style={{ width: `${fundingPct}%` }}
                  />
                </div>
                <div className="font-body text-sm text-slate-400">
                  ₹{(movie.raised_amount / 1000).toFixed(0)}K raised • {fundingPct.toFixed(0)}% funded
                </div>
              </div>

              {/* Investment Tiers */}
              <div className="mb-6">
                <label className="font-body text-sm font-medium mb-3 block">Select Tier</label>
                <div className="space-y-3">
                  {movie.investment_tiers.map((tier) => (
                    <button
                      key={tier.name}
                      data-testid={`tier-${tier.name.toLowerCase().replace(/\s+/g, '-')}-btn`}
                      onClick={() => {
                        setSelectedTier(tier);
                        setInvestAmount(tier.min_amount.toString());
                      }}
                      className={`w-full text-left p-4 rounded-sm border transition-all ${
                        selectedTier?.name === tier.name
                          ? 'border-gold bg-gold/10'
                          : 'border-white/10 bg-black/20 hover:bg-white/5'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="font-body font-bold">{tier.name}</div>
                        <div className="font-mono text-gold font-bold tabular-nums">{tier.royalty_percentage}%</div>
                      </div>
                      <div className="font-body text-xs text-slate-400 mb-2">{tier.description}</div>
                      <div className="font-body text-xs text-slate-400">
                        Min: ₹{tier.min_amount.toLocaleString('en-IN')}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Amount Input */}
              <div className="mb-6">
                <label className="font-body text-sm font-medium mb-2 block">Investment Amount</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-mono">₹</span>
                  <input
                    data-testid="invest-amount-input"
                    type="text"
                    inputMode="numeric"
                    value={investAmount}
                    onChange={(e) => setInvestAmount(e.target.value)}
                    className="w-full bg-black/20 border border-white/10 focus:border-gold/50 focus:ring-1 focus:ring-gold/50 rounded-sm h-12 text-white pl-8 pr-4 font-mono outline-none transition-colors tabular-nums"
                    placeholder="e.g. 50000  or  5L  or  1cr"
                  />
                </div>
                {investAmount && Number.isFinite(parseInrInput(investAmount)) && (
                  <div className="mt-2 font-mono text-xs text-gold">
                    = ₹{parseInrInput(investAmount).toLocaleString('en-IN')} ({formatInrFriendly(parseInrInput(investAmount))})
                  </div>
                )}
                {selectedTier && (
                  <div className="mt-1 font-body text-xs text-slate-400">
                    Minimum for {selectedTier.name}: ₹{selectedTier.min_amount.toLocaleString('en-IN')}
                  </div>
                )}
              </div>

              {/* Expected Returns */}
              {selectedTier && investAmount && Number.isFinite(parseInrInput(investAmount)) && (
                <div className="bg-gold/10 border border-gold/30 rounded-lg p-4 mb-6">
                  <div className="font-body text-xs text-gold mb-1">Expected Royalty Rate</div>
                  <div className="font-mono text-2xl font-bold text-gold tabular-nums">
                    {selectedTier.royalty_percentage}%
                  </div>
                  <div className="font-body text-xs text-slate-400 mt-2">
                    Based on movie revenue
                  </div>
                </div>
              )}

              {/* Invest Button */}
              <button
                data-testid="invest-submit-btn"
                onClick={handleInvest}
                disabled={investing || !selectedTier}
                className="w-full btn-primary py-4 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {(() => {
                  if (investing) return 'Redirecting to Stripe…';
                  const parsed = investAmount ? parseInrInput(investAmount) : null;
                  const chargeAmount = (parsed && Number.isFinite(parsed) && parsed > 0) ? parsed : selectedTier?.min_amount;
                  return `Invest ₹${(chargeAmount || 0).toLocaleString('en-IN')} via Stripe`;
                })()}
              </button>

              <p className="font-body text-xs text-center text-slate-400 mt-3">
                🔒 Funds held in escrow until movie reaches funding goal. Refunded otherwise.
              </p>

              {/* Fan Pledge Section */}
              <div className="mt-8 pt-6 border-t border-white/5">
                <div className="font-body text-xs uppercase tracking-wide text-slate-400 mb-3">
                  Not an investor? Back as a fan
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[1, 3, 6].map((m) => (
                    <button
                      key={m}
                      data-testid={`fan-pledge-${m}m-btn`}
                      onClick={() => handleFanPledge(m)}
                      disabled={investing}
                      className="bg-white/5 border border-white/10 hover:border-gold/50 rounded-sm py-3 px-2 text-center transition-all disabled:opacity-50"
                    >
                      <div className="font-mono text-base font-bold text-gold tabular-nums">₹{100 * m}</div>
                      <div className="font-body text-[10px] text-slate-400 uppercase tracking-wide">
                        {m} mo
                      </div>
                    </button>
                  ))}
                </div>
                <p className="font-body text-[11px] text-center text-slate-400 mt-3">
                  ₹100/month • Supports producer directly
                </p>
              </div>

              {!user && (
                <p className="font-body text-xs text-center text-slate-400 mt-4">
                  Please login to invest or back
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* PAPPA Settlement Card (shown if settled) */}
      {pappaSettlement && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          <div className="bg-card border border-emerald-500/30 rounded-lg p-6" data-testid="pappa-settlement-card">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <h3 className="font-heading text-xl font-bold">PAPPA SETTLEMENT <span className="text-emerald-300 text-sm">· ✓ Closed</span></h3>
              <span className="text-xs text-slate-400 font-body">Settled on {new Date(pappaSettlement.settled_at).toLocaleDateString()}</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div className="bg-white/[0.03] border border-white/5 rounded p-3">
                <div className="text-[10px] uppercase text-slate-400">OTT paid producer</div>
                <div className="font-mono text-base font-bold tabular-nums text-cyan-300">₹{formatInrFriendly(pappaSettlement.ott_received_inr)}</div>
              </div>
              <div className="bg-white/[0.03] border border-white/5 rounded p-3">
                <div className="text-[10px] uppercase text-slate-400">Returned to associates</div>
                <div className="font-mono text-base font-bold tabular-nums">₹{formatInrFriendly(pappaSettlement.total_principal_inr)}</div>
              </div>
              <div className="bg-white/[0.03] border border-white/5 rounded p-3">
                <div className="text-[10px] uppercase text-slate-400">Markup paid out (+{pappaSettlement.markup_pct}%)</div>
                <div className="font-mono text-base font-bold tabular-nums text-emerald-400">+₹{formatInrFriendly(pappaSettlement.total_markup_inr)}</div>
              </div>
              <div className="bg-white/[0.03] border border-white/5 rounded p-3">
                <div className="text-[10px] uppercase text-slate-400">Producer keeps</div>
                <div className="font-mono text-base font-bold tabular-nums text-gold">₹{formatInrFriendly(pappaSettlement.producer_keep_inr)}</div>
              </div>
            </div>
            <div className="text-xs uppercase tracking-wide text-slate-400 mb-2 font-body">Per-associate payouts ({pappaSettlement.associate_payouts.length})</div>
            <div className="space-y-1 max-h-64 overflow-y-auto" data-testid="pappa-payouts-list">
              {pappaSettlement.associate_payouts.map(p => (
                <div key={p.investment_id} className="flex justify-between items-center bg-white/[0.02] border border-white/5 rounded px-3 py-2 text-sm" data-testid={`payout-${p.investor_id}`}>
                  <span className="font-body">{p.investor_name}</span>
                  <span className="font-mono tabular-nums text-xs text-slate-300">
                    ₹{formatInrFriendly(p.principal_inr)} <span className="text-emerald-400">+ ₹{formatInrFriendly(p.markup_inr)}</span> = <span className="text-gold font-bold">₹{formatInrFriendly(p.total_payout_inr)}</span>
                  </span>
                </div>
              ))}
            </div>
            {pappaSettlement.notes && <p className="text-xs italic text-slate-400 mt-3">Note: {pappaSettlement.notes}</p>}
          </div>
        </div>
      )}

      {/* PAPPA Settle modal (producer/admin) */}
      {showSettleModal && (
        <Modal onClose={() => setShowSettleModal(false)} title="Settle PAPPA">
          <div className="bg-card border border-white/10 rounded-lg max-w-md w-full p-6" data-testid="pappa-settle-modal">
            <h3 className="font-heading text-xl font-bold mb-2">Settle PAPPA</h3>
            <p className="font-body text-sm text-slate-400 mb-4">Enter the actual amount the OTT partner paid you. We'll auto-distribute principal + {movie?.pappa?.markup_pct}% markup to all {movie?.pappa?.raised_amount_inr ? 'PAPPA associates' : 'associates'}.</p>
            <p className="font-body text-xs text-slate-400 mb-1">Minimum required: <span className="text-amber-300 font-mono">₹{formatInrFriendly((movie?.pappa?.raised_amount_inr || 0) * (1 + (movie?.pappa?.markup_pct || 20) / 100))}</span></p>
            <div className="space-y-3">
              <div>
                <label className="font-body text-xs text-slate-400">OTT received (INR) *</label>
                <input data-testid="settle-amount-input" className="admin-input mt-1" value={settleAmount} onChange={e => setSettleAmount(e.target.value)} placeholder="e.g., 10 crore" />
              </div>
              <div>
                <label className="font-body text-xs text-slate-400">Notes (optional)</label>
                <textarea data-testid="settle-notes-input" rows={2} className="admin-input mt-1" value={settleNotes} onChange={e => setSettleNotes(e.target.value)} placeholder="e.g., OTT paid full PO on 2026-02-01" />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setShowSettleModal(false)} className="btn-secondary text-sm py-2">Cancel</button>
              <button
                data-testid="settle-confirm-btn"
                onClick={async () => {
                  const amt = parseInrInput(settleAmount);
                  if (!Number.isFinite(amt) || amt <= 0) { toast.error('Enter a valid amount'); return; }
                  try {
                    const res = await axios.post(`${BACKEND_URL}/api/movies/${id}/pappa/settle`, { ott_received_inr: amt, notes: settleNotes || undefined }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
                    toast.success(`Settled — ₹${formatInrFriendly(res.data.total_principal_inr + res.data.total_markup_inr)} distributed to ${res.data.associate_payouts.length} associates.`);
                    setShowSettleModal(false);
                    fetchMovie();
                    fetchPappaSettlement();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Settle failed');
                  }
                }}
                className="px-4 py-2 text-sm rounded-lg bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/30 transition-colors"
              >Confirm settle</button>
            </div>
          </div>
        </Modal>
      )}

      {/* PAPPA Invest modal (investor) */}
      {pappaInvestOpen && (
        <Modal onClose={() => setPappaInvestOpen(false)} title="Back as PAPPA Associate">
          <div className="bg-card border border-white/10 rounded-lg max-w-md w-full p-6" data-testid="pappa-invest-modal">
            <h3 className="font-heading text-xl font-bold mb-2">Back as PAPPA Associate</h3>
            <p className="font-body text-sm text-slate-400 mb-2">Contribute to <span className="text-gold">{movie?.pappa?.ott_partner_name}'s PO</span> on this film. Earn a fixed +{movie?.pappa?.markup_pct}% markup when OTT pays.</p>
            <p className="font-body text-xs text-slate-400 mb-3">
              Remaining in this raise: <span className="text-amber-300 font-mono">₹{formatInrFriendly((movie?.pappa?.raise_amount_inr || 0) - (movie?.pappa?.raised_amount_inr || 0))}</span> · Minimum: ₹10,000
            </p>
            <div>
              <label className="font-body text-xs text-slate-400">Amount to invest (INR) *</label>
              <input data-testid="pappa-invest-amount-input" className="admin-input mt-1" value={pappaInvestAmount} onChange={e => setPappaInvestAmount(e.target.value)} placeholder="e.g., 5 lakh" />
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setPappaInvestOpen(false)} className="btn-secondary text-sm py-2">Cancel</button>
              <button
                data-testid="pappa-invest-confirm-btn"
                onClick={async () => {
                  const amt = parseInrInput(pappaInvestAmount);
                  if (!Number.isFinite(amt) || amt < 10000) { toast.error('Minimum ₹10,000'); return; }
                  try {
                    const res = await axios.post(`${BACKEND_URL}/api/payments/pappa/checkout`, {
                      movie_id: id, amount: amt, origin_url: window.location.origin,
                    }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
                    window.location.href = res.data.url;
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Checkout failed');
                  }
                }}
                className="btn-primary text-sm py-2"
              >Continue to Stripe</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default MovieDetail;